import { NgModule, ModuleWithProviders } from '@angular/core'
import { CommonModule } from '@angular/common'
import { HttpModule } from '@angular/http'
import { HttpClientModule } from '@angular/common/http'
import { RouterModule } from '@angular/router'
import { MessageService, ModuleStateService, AppMetricsService, AuthenticationService, EnvironmentService } from '@mprep/shared/core/services'
import { TestExecution, BaseModel } from '@mprep/shared/data/models'
import { GuaranteedFindDecoratorFactory } from '@mprep/shared/lib/model-repository'
import {
  Utils,
  AppInjector,
  DomUtils,
  RedirectHelper,
  AppContext,
  HttpHelper,
  ObjectHelper,
  TreeUtils,
  DateHelper,
} from '@mprep/shared/lib/helpers'
import {
  SafeHtmlPipe,
  DialogComponent,
  FlowplayerComponent,
  FlowplayerService,
  FlowplayerClipFactory,
  FlowplayerModelRendererComponent,
  FlowplayerNormalRendererComponent,
  DataServiceProvider,
  OperationServiceProvider
} from '@mprep/shared/angular'
import { AuthGuard } from '@mprep/shared/lib/guards'
import { ProvidersBuilder } from '@mprep/shared/lib/providers.builder'
import * as inflection from 'inflection'
import { Ng2Bs3ModalModule } from 'ng2-bs3-modal/ng2-bs3-modal'

import { Cookie } from 'ng2-cookies/ng2-cookies'

import * as components from './components'
import * as directives from './directives'
import * as pipes from './pipes'
import * as guards from './guards'

// MP-TODO: This may be unnecessary
import { UserTask } from '@mprep/shared/data/models'

import {
  IntroService,
  MenuItemFactory,
  MenuService,
  PathNodeCalculationService,
  NavigationService,
  MessagingService,
  PathFilterService,
  PathResetService,
  NavigationStatusService,
  AppStateService,
  UIExpirationMessageService,
  PopupNotificationService,
  ResourceGroupService
} from './services'

import {
  Window,
  API_ROOT,
  LOGOUT_PATH,
  LOGIN_URL,
  LOGOUT_URL,
  MutationObserverFactory,
  APP_VERSION,
  TEST_LINES
} from './helpers'

import {
  ActivePathConfig,
  CartItemAssociationConfig,
  PathConfigurationConfig,
  PathConfirmationConfig,
  ResourceConfig,
  ResourceSecureUriConfig,
  UserTaskConfig,
  TaskConfig,
  PathNodeConfig,
  ExpirationMessageConfig,
  GraphqlResultConfig,
  LsacResultConfig,
  TestConfig,
  TestExecutionConfig,
  TestExecutionResultConfig,
  ObservableUtils,
  UserConfig,
  AnalyticsDataProvider,
  FlowplayerRendererDirective,
  ProductRegistrationConfig
} from '@mprep/shared'
import { MenuItemConfig } from 'app/shared/services/model-repository/mappings/menu-item.config'
import { ResourceGroupConfig } from 'app/shared/services/model-repository/mappings/resource-group.config'
import { UriFactory } from '@mprep/shared/lib/core/factories'
import { ProductRecommendationService, KtpEnrollmentRecommendationService } from '@mprep/shared/lib/data/services/product-recommendation'

const libEndpointMappings = {}
const entityMappingConfigs = []
const formMappings = []
const entityListConfigs = []
const presenterClasses = []
const appInjectorClasses = []
const registeredModels = [TestExecution, UserTask] as Array<{new(...args): BaseModel}>
const libProviders = new ProvidersBuilder(
  API_ROOT,
  libEndpointMappings,
  entityMappingConfigs,
  registeredModels,
  formMappings,
  entityListConfigs,
  presenterClasses,
  APP_VERSION,
  appInjectorClasses,
  LOGIN_URL,
  LOGOUT_URL,
  { 'USERS_API_URL': 'https://apis.manhattanprep.com/v1/users' }
).build()

const libFilteredProviders = [
  libProviders.find((provider) => provider.provide === Utils),
  libProviders.find((provider) => provider.provide === DomUtils),
  libProviders.find((provider) => provider.provide === AppInjector),
  libProviders.find((provider) => provider.provide === AppContext),
  libProviders.find((provider) => provider.provide === AppMetricsService),
  libProviders.find((provider) => provider.provide === AuthGuard),
  libProviders.find((provider) => provider.provide === AuthenticationService),
  libProviders.find((provider) => provider.provide === GuaranteedFindDecoratorFactory),
  libProviders.find((provider) => provider.provide === ModuleStateService),
  libProviders.find((provider) => provider.provide === HttpHelper),
  libProviders.find((provider) => provider.provide === RedirectHelper),
  libProviders.find((provider) => provider.provide === FlowplayerService),
  libProviders.find((provider) => provider.provide === FlowplayerClipFactory),
  libProviders.find((provider) => provider.provide === ObjectHelper),
  libProviders.find((provider) => provider.provide === ProductRecommendationService),
  libProviders.find((provider) => provider.provide === KtpEnrollmentRecommendationService),
  libProviders.find((provider) => provider.provide === TreeUtils),
  libProviders.find((provider) => provider.provide === DateHelper),
  libProviders.find((provider) => provider.provide === EnvironmentService),
]
const libPipes = [
  SafeHtmlPipe
]
const SharedComponents = toArray(components)
.concat([DialogComponent, FlowplayerComponent, FlowplayerModelRendererComponent,FlowplayerNormalRendererComponent])
const sharedDirectives = toArray(directives).concat([FlowplayerRendererDirective])
const sharedPipes = toArray(pipes).concat(libPipes)
const sharedGuards = toArray(guards)
const messageService = new MessageService(new Utils(window, inflection))

const dataServiceProvider = new DataServiceProvider(
  API_ROOT, [
  ActivePathConfig,
  CartItemAssociationConfig,
  PathConfigurationConfig,
  PathConfirmationConfig,
  ResourceConfig,
  ResourceSecureUriConfig,
  UserTaskConfig,
  PathNodeConfig,
  TestExecutionConfig,
  TestExecutionResultConfig,
  TestConfig,
  TaskConfig,
  UserConfig,
  ProductRegistrationConfig,
  // App-only configs
  MenuItemConfig,
  ResourceGroupConfig
])

const operationServiceProvider = new OperationServiceProvider(
  API_ROOT, [
  ExpirationMessageConfig,
  GraphqlResultConfig,
  LsacResultConfig
])

const serviceProviders = dataServiceProvider.createAngularProviders().concat(
  operationServiceProvider.createAngularProviders()
)
const sharedProviders = [
  ObservableUtils,
  { provide: MessageService, useValue: messageService },
  { provide: AnalyticsDataProvider, useValue: AnalyticsDataProvider.instance },
  IntroService,
  MenuItemFactory,
  MenuService,
  PathNodeCalculationService,
  NavigationService,
  ResourceGroupService,
  MessagingService,
  PathFilterService,
  Window,
  ...sharedGuards,
  PathResetService,
  NavigationStatusService,
  AppStateService,
  UIExpirationMessageService,
  PopupNotificationService,
  MutationObserverFactory,
  { provide: Cookie, useValue: Cookie },
  UriFactory,
  ...libFilteredProviders,
  ...serviceProviders
]

@NgModule({
  declarations: [
    ...SharedComponents,
    ...sharedDirectives,
    ...sharedPipes,
  ],
  imports: [
    CommonModule,
    Ng2Bs3ModalModule,
    HttpModule,
    HttpClientModule,
    RouterModule,
  ],
  exports: [
    Ng2Bs3ModalModule,
    ...SharedComponents,
    ...sharedDirectives,
    ...sharedPipes
  ],
  entryComponents: [
    FlowplayerModelRendererComponent,
    FlowplayerNormalRendererComponent
  ]
})
export class SharedModule {
  public static forRoot(): ModuleWithProviders {
    return {
      ngModule: SharedModule,
      providers: [...sharedProviders]
    }
  }
}

function toArray(object) {
  return Object.values(object)
}



// WEBPACK FOOTER //
// ./src/app/shared/shared.module.ts